import { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../../../styles/quill-custom.css';
import { ArrowLeft, Save } from 'lucide-react';
import {
  modeloContratoService,
  ModeloContratoInput,
} from '../../../../services/contratoService';

interface ModeloContratoFormProps {
  modeloId?: string;
  onBack: () => void;
  onSave: () => void;
}

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ size: ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet', 'indent',
  'align',
  'link',
];

export function ModeloContratoForm({ modeloId, onBack, onSave }: ModeloContratoFormProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<ModeloContratoInput>({
    titulo: '',
    tipoNegocio: '',
    conteudo: '',
  });
  const quillRef = useRef<ReactQuill>(null);

  const isEditing = !!modeloId;

  useEffect(() => {
    if (modeloId) {
      carregarModelo();
    }
  }, [modeloId]);

  async function carregarModelo() {
    try {
      setLoading(true);
      const modelo = await modeloContratoService.getById(modeloId!);
      setForm({
        titulo: modelo.titulo,
        tipoNegocio: modelo.tipoNegocio || '',
        conteudo: modelo.conteudo,
      });
    } catch (error) {
      console.error('Erro ao carregar modelo:', error);
      alert('Erro ao carregar modelo');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!form.titulo.trim()) {
      alert('Título é obrigatório');
      return;
    }

    const conteudoSemHtml = form.conteudo.replace(/<[^>]*>/g, '').trim();
    if (!conteudoSemHtml) {
      alert('Conteúdo é obrigatório');
      return;
    }

    try {
      setSaving(true);

      if (isEditing) {
        await modeloContratoService.update(modeloId!, form);
      } else {
        await modeloContratoService.create(form);
      }

      onSave();
    } catch (error) {
      console.error('Erro ao salvar modelo:', error);
      alert('Erro ao salvar modelo');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="p-2 hover:bg-muted rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            {isEditing ? 'Editar Template' : 'Novo Template de Contrato'}
          </h2>
          <p className="text-muted-foreground">
            {isEditing ? 'Atualize o template' : 'Crie um template reutilizável para contratos'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Metadados */}
        <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6 mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Título */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Título do Template *
              </label>
              <input
                type="text"
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                placeholder="Ex: Contrato Padrão - Prestação de Serviços IT"
                required
              />
            </div>

            {/* Tipo de Negócio */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Tipo de Negócio
              </label>
              <input
                type="text"
                value={form.tipoNegocio}
                onChange={(e) => setForm({ ...form, tipoNegocio: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground"
                placeholder="Ex: Manutenção, Consultoria, Suporte"
              />
            </div>
          </div>

          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <p className="text-sm text-foreground mb-2">
              <strong>💡 Rich Text Editor:</strong> Use a barra de ferramentas para formatar seu template:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-muted-foreground">
              <div>• <strong>Títulos</strong> (H1, H2, H3)</div>
              <div>• <strong>Negrito</strong>, <em>Itálico</em></div>
              <div>• <span style={{textDecoration: 'underline'}}>Sublinhado</span>, <span style={{textDecoration: 'line-through'}}>Riscado</span></div>
              <div>• Listas numeradas e com marcadores</div>
              <div>• Alinhamento (esquerda, centro, direita)</div>
              <div>• Cores de texto e fundo</div>
              <div>• Tamanhos de fonte</div>
              <div>• Links e limpeza de formatação</div>
            </div>
            <p className="text-sm text-foreground mt-2">
              Ao criar um contrato, você poderá selecionar este template e editá-lo livremente.
            </p>
          </div>
        </div>

        {/* Editor */}
        <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl overflow-hidden mb-6">
          <div className="border-b border-border px-4 py-3">
            <h3 className="font-medium text-foreground">Conteúdo do Template *</h3>
            <p className="text-sm text-muted-foreground">
              Digite ou cole o texto completo do contrato
            </p>
          </div>
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={form.conteudo}
            onChange={(val) => setForm((f) => ({ ...f, conteudo: val }))}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            className="quill-template-editor h-[600px]"
            placeholder="Digite ou cole o conteúdo completo do contrato..."
          />
        </div>

        {/* Ações */}
        <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-4 flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 text-foreground hover:bg-muted rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Salvando...' : 'Salvar Template'}
          </button>
        </div>
      </form>
    </div>
  );
}
